import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Check, MessageCircle, ChevronLeft, Flame, CookingPot, Microwave, Soup,
  Sandwich, Target, Lightbulb, UtensilsCrossed, ArrowUpRight, Users, Snowflake, Award,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useProduct, useSite } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { formatPrice, lineEstimate } from "@/lib/site";
import { cn } from "@/lib/utils";
import { useOrderList } from "@/context/OrderListContext";
import MeatIdentityBars from "@/components/products/MeatIdentityBars";
import ProductCard from "@/components/products/ProductCard";
import QuantityPicker from "@/components/order/QuantityPicker";

const METHOD_ICONS = { Mangal: Flame, Tava: CookingPot, Fırın: Microwave, Tencere: Soup, Burger: Sandwich };

const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 border border-dashed border-bordo/30 bg-paper px-3.5 py-1.5 text-sm text-charcoal/85">
    {children}
  </span>
);

const SectionTitle = ({ kicker, children, testid }) => (
  <div className="mb-6">
    {kicker && <span className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-bordo">{kicker}</span>}
    <h2 data-testid={testid} className="font-serif text-3xl sm:text-4xl text-charcoal">{children}</h2>
  </div>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const { data: product, isLoading } = useProduct(slug);
  const { data: site } = useSite();
  const { addItem, isInList } = useOrderList();
  const [qty, setQty] = useState("1 kg");

  if (isLoading || !product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-32 text-center font-mono text-sm text-charcoal/50" data-testid="product-loading">
        Ürün yükleniyor...
      </div>
    );
  }

  const inList = isInList(product.slug);
  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} (${qty}) listene eklendi`);
  };

  const askUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba ${site.brand}, "${product.name}" ürünü hakkında bilgi almak istiyorum.`)}`
    : "#";

  return (
    <div data-testid="product-detail-page" className="bg-cream pb-24">
      <Seo
        title={product.name}
        description={product.short_description || product.description}
        image={product.image}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: [(process.env.REACT_APP_SITE_URL || window.location.origin) + product.image],
          description: product.short_description || product.description,
          category: product.category,
          brand: { "@type": "Brand", name: "Ertan Kasap" },
          ...(product.price != null && {
            offers: {
              "@type": "Offer",
              price: product.price,
              priceCurrency: "TRY",
              availability: "https://schema.org/InStock",
              url: window.location.href,
            },
          }),
        }}
      />
      {/* HERO */}
      <section className="border-b border-dashed border-bordo/30">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
          <Link to="/urunler" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-charcoal/50 hover:text-bordo transition-colors">
            <ChevronLeft className="h-4 w-4" /> Tüm Ürünler
          </Link>
        </div>
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-12">
          <div className="relative overflow-hidden border-4 border-charcoal">
            <img src={product.image} alt={product.name} className="aspect-[4/5] w-full object-cover" />
            <span className="absolute left-0 top-5 bg-bordo px-4 py-1.5 font-mono text-xs uppercase tracking-[0.15em] text-cream">{product.category}</span>
            {product.tags?.length > 0 && (
              <div className="absolute bottom-4 left-4 flex flex-wrap gap-1.5">
                {product.tags.map((t) => (
                  <span key={t} className="bg-cream/90 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide text-bordo">{t}</span>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center">
            {product.badge && (
              <span data-testid="product-detail-badge" className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full border border-copper/30 px-2.5 py-[3px] font-mono text-[10px] uppercase tracking-[0.2em] text-copper/90">
                <Award className="h-3 w-3 text-copper/70" strokeWidth={1.5} /> {product.badge}
              </span>
            )}
            <h1 className="font-serif text-5xl tracking-tight text-charcoal sm:text-6xl">{product.name}</h1>
            <p className="mt-4 text-lg leading-relaxed text-[#6b645c]">{product.description}</p>

            {product.who_prefers?.length > 0 && (
              <div data-testid="section-who-prefers" className="mt-7 border border-dashed border-copper/40 bg-paper p-5">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-copper">Bu ürünü kimlere öneriyoruz?</span>
                <ul className="mt-3 grid gap-2.5 sm:grid-cols-2">
                  {product.who_prefers.map((w) => (
                    <li key={w} data-testid={`who-prefers-${w}`} className="flex items-center gap-2 text-sm text-charcoal/85">
                      <Check className="h-4 w-4 flex-shrink-0 text-bordo" /> {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-7 border border-dashed border-bordo/30 bg-paper p-5">
              <span className="font-mono text-xs uppercase tracking-wider text-charcoal/50">Miktar seç ve listene ekle</span>
              {product.price != null && (
                <div className="mt-3 flex items-baseline justify-between border-b border-dashed border-bordo/20 pb-3">
                  <span className="font-mono text-xs text-charcoal/55">
                    {formatPrice(product.price)} <span className="text-charcoal/40">/ {product.unit || "kg"}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/45">Seçili miktar ({qty})</span>
                    <span data-testid="detail-selected-price" className="font-serif text-3xl leading-none text-bordo">
                      ≈ {formatPrice(Math.round(lineEstimate({ price: product.price, unit: product.unit, quantity: qty }) || product.price))}
                    </span>
                  </span>
                </div>
              )}
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <QuantityPicker testid="detail-quantity" value={qty} onChange={setQty} />
                <button
                  data-testid="detail-add-to-order"
                  onClick={handleAdd}
                  className={cn(
                    "inline-flex flex-1 items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-bold uppercase tracking-wide transition-colors",
                    inList ? "bg-charcoal text-cream" : "bg-bordo text-cream hover:bg-bordo-dark"
                  )}
                >
                  {inList ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {inList ? "Listene Eklendi" : "Sipariş Listeme Ekle"}
                </button>
              </div>
              <a
                href={askUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-whatsapp-ask"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-sm border border-[#25D366] px-5 py-3 text-sm font-bold uppercase tracking-wide text-[#1a9e4b] transition-colors hover:bg-[#25D366] hover:text-charcoal"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp’tan Sor
              </a>
            </div>
            <p className="mt-3 font-mono text-sm text-[#8C857D]">{product.serving}</p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* ET KİMLİĞİ + KULLANIM */}
        <section className="grid gap-10 py-14 lg:grid-cols-2">
          <div>
            <SectionTitle kicker="Et Kimliği" testid="section-identity">Lezzet Profili</SectionTitle>
            <div className="border border-dashed border-bordo/30 bg-paper p-6">
              <MeatIdentityBars product={product} />
            </div>
          </div>
          <div>
            <SectionTitle kicker="Kullanım" testid="section-bestfor">En İyi Kullanım Alanları</SectionTitle>
            <div className="flex flex-wrap gap-2.5">
              {product.best_for?.map((b) => (<Chip key={b}><Target className="h-4 w-4 text-bordo" /> {b}</Chip>))}
            </div>
            <h3 className="mt-8 mb-4 font-serif text-2xl text-charcoal">Ne İçin Uygun?</h3>
            <div className="flex flex-wrap gap-2.5">
              {product.suitable_for?.map((s) => (<Chip key={s}><UtensilsCrossed className="h-4 w-4 text-bordo" /> {s}</Chip>))}
            </div>
          </div>
        </section>

        {/* PİŞİRME TEKNİKLERİ */}
        <section className="border-t border-dashed border-bordo/30 py-14">
          <SectionTitle kicker="Mutfak" testid="section-techniques">Pişirme Teknikleri</SectionTitle>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {product.techniques?.map((t) => {
              const m = t.title.split(" ")[0];
              const Icon = METHOD_ICONS[m] || Flame;
              return (
                <div key={t.title} className="border border-dashed border-bordo/30 bg-paper p-6">
                  <span className="flex h-11 w-11 items-center justify-center border border-dashed border-bordo/40">
                    <Icon className="h-5 w-5 text-bordo" />
                  </span>
                  <h4 className="mt-4 font-serif text-xl text-charcoal">{t.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-[#8C857D]">{t.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* PÜF NOKTALARI */}
        <section className="border-t border-dashed border-bordo/30 py-14">
          <SectionTitle kicker="Ustadan" testid="section-tips">Püf Noktaları</SectionTitle>
          <div className="grid gap-4 sm:grid-cols-2">
            {product.tips?.map((tip, i) => (
              <div key={i} className="flex gap-4 border border-dashed border-bordo/30 bg-paper p-5">
                <Lightbulb className="h-5 w-5 flex-shrink-0 text-copper" />
                <p className="text-sm leading-relaxed text-charcoal/85">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* KAÇ KİŞİLİK & SAKLAMA */}
        <section className="grid gap-6 border-t border-dashed border-bordo/30 py-14 md:grid-cols-2">
          <div data-testid="section-serving" className="border border-dashed border-bordo/30 bg-paper p-7">
            <span className="flex h-11 w-11 items-center justify-center border border-dashed border-bordo/40">
              <Users className="h-5 w-5 text-bordo" />
            </span>
            <h3 className="mt-4 font-serif text-2xl text-charcoal">Kaç Kişilik Çıkar?</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#6b645c]">{product.serving}</p>
          </div>
          <div data-testid="section-storage" className="border border-dashed border-bordo/30 bg-paper p-7">
            <span className="flex h-11 w-11 items-center justify-center border border-dashed border-bordo/40">
              <Snowflake className="h-5 w-5 text-bordo" />
            </span>
            <h3 className="mt-4 font-serif text-2xl text-charcoal">Saklama Önerileri</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#6b645c]">{product.storage}</p>
          </div>
        </section>

        {/* YANINA NE GİDER */}
        {product.recommended_products?.length > 0 && (
          <section className="border-t border-dashed border-bordo/30 py-14">
            <SectionTitle kicker="Sofranı Tamamla" testid="section-recommended">Bunu Alanlar Yanında Bu Lezzetleri de Denedi</SectionTitle>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {product.recommended_products.map((rp) => (<ProductCard key={rp.slug} product={rp} />))}
            </div>
          </section>
        )}

        {/* ALTERNATİFLER */}
        {product.alternative_products?.length > 0 && (
          <section className="border-t border-dashed border-bordo/30 py-14">
            <SectionTitle kicker="Belki Bunlar" testid="section-alternatives">Alternatif Ürünler</SectionTitle>
            <div className="grid gap-5 sm:grid-cols-3">
              {product.alternative_products.map((alt) => (
                <Link key={alt.product.slug} to={`/urun/${alt.product.slug}`} className="group flex items-center gap-4 border border-dashed border-bordo/30 bg-paper p-4 transition-colors hover:border-bordo">
                  <img src={alt.product.image} alt={alt.product.name} className="h-16 w-16 object-cover" />
                  <div className="min-w-0">
                    <span className="block font-mono text-[11px] uppercase tracking-wide text-copper">{alt.label}</span>
                    <span className="block font-serif text-xl text-charcoal group-hover:text-bordo transition-colors">{alt.product.name}</span>
                    <span className="mt-1 inline-flex items-center gap-1 font-mono text-[11px] text-charcoal/50">İncele <ArrowUpRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* KASABA SOR — büyük dönüşüm CTA */}
        <section data-testid="section-ask-butcher" className="border-t border-dashed border-bordo/30 py-14">
          <div className="flex flex-col items-center bg-bordo px-6 py-12 text-center sm:px-12 sm:py-16">
            <MessageCircle className="h-10 w-10 text-cream/70" strokeWidth={1.4} />
            <h2 className="mt-5 font-serif text-3xl leading-tight text-cream sm:text-4xl">
              {product.name} size uygun mu? Kasaba sorun.
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-cream/80">
              Gramaj, pişirme yöntemi ya da sofranıza uygunluğu hakkında aklınıza takılan her şeyi
              WhatsApp’tan yazın; ustamız hemen yardımcı olsun.
            </p>
            <a
              href={askUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="detail-ask-butcher-cta"
              className="mt-8 inline-flex items-center gap-2.5 rounded-sm bg-cream px-8 py-4 text-base font-bold uppercase tracking-wide text-bordo transition-transform hover:scale-[1.02]"
            >
              <MessageCircle className="h-5 w-5" /> Kasabıma Danış
            </a>
          </div>
        </section>

        {/* SSS */}
        <section className="border-t border-dashed border-bordo/30 py-14">
          <SectionTitle kicker="Merak Edilenler" testid="section-faqs">Sık Sorulan Sorular</SectionTitle>
          <Accordion type="single" collapsible className="border border-dashed border-bordo/30 bg-paper px-5">
            {product.faqs?.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-bordo/15">
                <AccordionTrigger className="text-left font-medium text-charcoal hover:text-bordo">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#6b645c] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>
    </div>
  );
}
